import { useState, useEffect } from 'react'
import {
  Image as HeroImage,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Chip,
} from '@heroui/react'

import { resolveGifUrl } from '../../gifs/services'
import { gifDisplayTitle, gifDisplayChips } from '../../gifs/types'
import type { Gif } from '../../gifs/types'

interface GifThumbnailProps {
  gif: Gif
  className?: string
}

export function GifThumbnail({ gif, className }: GifThumbnailProps) {
  const [resolvedUrl, setResolvedUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    let isMounted = true

    const resolveUrl = async () => {
      try {
        setIsLoading(true)
        setHasError(false)
        const url = await resolveGifUrl(gif.gifUrl)
        if (isMounted) {
          setResolvedUrl(url)
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Error resolving GIF URL:', error)
        if (isMounted) {
          setHasError(true)
          setIsLoading(false)
        }
      }
    }

    resolveUrl()

    return () => {
      isMounted = false
    }
  }, [gif.gifUrl])

  if (isLoading) {
    return (
      <div
        className={`aspect-square bg-slate-200 dark:bg-slate-700 rounded overflow-hidden animate-pulse flex items-center justify-center ${className}`}
      >
        <div className="text-slate-500 text-xs">Loading...</div>
      </div>
    )
  }

  if (hasError) {
    return (
      <div
        className={`aspect-square bg-slate-200 dark:bg-slate-700 rounded overflow-hidden flex items-center justify-center ${className}`}
      >
        <div className="text-slate-500 text-xs text-center px-1">
          <div>Error</div>
          <div className="text-xs mt-1">{gif.id.slice(-8)}</div>
        </div>
      </div>
    )
  }

  return (
    <Popover placement="top" showArrow={true}>
      <PopoverTrigger>
        <div
          className={`relative aspect-square bg-slate-100 dark:bg-slate-800 rounded overflow-hidden group cursor-pointer ${className}`}
        >
          <HeroImage
            src={resolvedUrl}
            alt={`GIF ${gif.id}`}
            className="w-full h-full object-cover"
            classNames={{
              wrapper: 'w-full h-full',
              img: 'w-full h-full object-cover',
            }}
          />

          {/* Hover overlay with ID */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="text-white text-xs font-mono text-center px-1">
              {gif.id.slice(-8)}
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="px-1 py-2">
          <div className="text-small font-bold mb-2">GIF Details</div>

          {/* Title */}
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Description:
            </div>
            <div className="text-xs text-slate-800 dark:text-slate-200">
              {gifDisplayTitle(gif)}
            </div>
          </div>

          {/* Tags (breadthTags, legacy fallback) */}
          {gifDisplayChips(gif).length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Tags:
              </div>
              <div className="flex flex-wrap gap-1">
                {gifDisplayChips(gif)
                  .slice(0, 6)
                  .map((tag, index) => (
                    <Chip key={index} size="sm" variant="flat" color="default">
                      {tag}
                    </Chip>
                  ))}
              </div>
            </div>
          )}

          {/* Reference Info */}
          {(gif.reference.source || gif.reference.keywords.length > 0) && (
            <div className="mb-3">
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Reference:
              </div>
              {gif.reference.source && (
                <div className="text-xs text-slate-800 dark:text-slate-200 mb-1">
                  <span className="font-medium">Source:</span>{' '}
                  {gif.reference.source}
                  {gif.reference.year && ` (${gif.reference.year})`}
                </div>
              )}
              {gif.reference.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {gif.reference.keywords.slice(0, 4).map((keyword, index) => (
                    <Chip key={index} size="sm" variant="light">
                      {keyword}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-2 gap-2">
              <div>ID: {gif.id.slice(-8)}</div>
              <div>Duration: {gif.durationSeconds}s</div>
              <div>Rating: {gif.explicitRating}</div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
